import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { CalendarComingSoonDialog } from "@/components/momentum/calendar-coming-soon";
import {
  CalendarConnectedDialog,
  ConnectCalendarDialog,
  type ConnectNotice,
} from "@/components/calendar/connect-dialogs";
import { DarkPill, IconTile, OutlinePill, calendarDialogClass, calendarOverlayClass } from "@/components/calendar/ui";
import { showPillToast } from "@/components/calendar/pill-toast";
import {
  CALENDAR_CONNECTION_KEY,
  useCalendarConnection,
  useDisconnectCalendar,
  useRemoveSchedule,
  useStartGoogleConnect,
  useStepSchedules,
} from "@/hooks/use-calendar";
import {
  clearCalendarResult,
  clearHandoff,
  readCalendarResult,
  readHandoff,
  returnUrl,
  saveHandoff,
} from "@/lib/calendar-handoff";
import { calendarErrorCode } from "@/lib/calendar-api";
import { formatDay, nextEvent, upcomingEvents, type CalendarConnection, type StepSchedule } from "@/lib/calendar";
import { cn } from "@/lib/utils";

/** The parts of a Next Step the calendar journey needs. */
export interface StepRef {
  id: string;
  journalId: string;
  text: string;
}

/** `again` adds a second event; `reschedule` moves the next one; `rhythm` changes a routine. */
export type PlanMode = "new" | "again" | "reschedule" | "rhythm";

export function planPath(step: Pick<StepRef, "id" | "journalId">, mode: PlanMode = "new"): string {
  return `/momentum/plan/${step.journalId}/${step.id}${mode === "new" ? "" : `?mode=${mode}`}`;
}

interface CalendarFlowValue {
  /** False until the calendar API is deployed; entry points then show "coming soon". */
  available: boolean;
  connection: CalendarConnection | null;
  schedules: Map<string, StepSchedule>;
  /** "Add to Calendar" on a step: connect first if needed, else straight to Find a time. */
  addToCalendar: (step: StepRef) => void;
  /** Connect without a step (Momentum header, Settings). */
  connect: () => void;
  plan: (step: StepRef, mode?: PlanMode) => void;
  /** Asks first, then removes the step's upcoming events from the calendar. */
  remove: (step: StepRef) => void;
}

const CalendarFlowContext = createContext<CalendarFlowValue | null>(null);

export function useCalendarFlow(): CalendarFlowValue {
  const value = useContext(CalendarFlowContext);
  if (!value) throw new Error("useCalendarFlow must be used inside <CalendarFlowProvider>");
  return value;
}

/**
 * A "Continue to Google" mutation stays `isSuccess` while the browser leaves for Google.
 * If the person presses Back there and the browser restores this page from its
 * back/forward cache, reset it so its button stops spinning.
 */
export function useResetOnBackForward(mutation: { reset: () => void }): void {
  const { reset } = mutation;
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) reset();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [reset]);
}

/** Why removing a step's dates failed; the dialog stays open to try again. */
type RemoveError = "connection_ended" | "failed" | null;

/**
 * Runs the connect journey for a page: the J02 / J04 dialogs, the hand-off around
 * Google's same-tab screen, and landing back with `?calendar=`.
 */
export function CalendarFlowProvider({ origin, children }: { origin: "momentum" | "settings"; children: ReactNode }) {
  const [location, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { connection, available } = useCalendarConnection();
  const { schedules } = useStepSchedules(available);
  const startConnect = useStartGoogleConnect();
  const disconnect = useDisconnectCalendar();
  const removeSchedule = useRemoveSchedule();
  useResetOnBackForward(startConnect);

  const [connectFor, setConnectFor] = useState<{ step?: StepRef; notice: ConnectNotice } | null>(null);
  const [connectedFor, setConnectedFor] = useState<{ step?: StepRef } | null>(null);
  const [removing, setRemoving] = useState<StepRef | null>(null);
  const [removeError, setRemoveError] = useState<RemoveError>(null);
  const [comingSoon, setComingSoon] = useState(false);

  // Back from Google's screen.
  useEffect(() => {
    const result = readCalendarResult();
    if (!result) return;
    const handoff = readHandoff();
    clearCalendarResult();
    if (handoff?.origin === "plan") return; // Find a time handles its own return.
    const step =
      handoff?.actionId && handoff.journalId
        ? { id: handoff.actionId, journalId: handoff.journalId, text: handoff.stepText ?? "" }
        : undefined;
    clearHandoff();
    if (result === "connected") {
      queryClient.invalidateQueries({ queryKey: CALENDAR_CONNECTION_KEY });
      setConnectedFor({ step });
    } else {
      setConnectFor({ step, notice: result === "missing_events" ? "missing_events" : "cancelled" });
    }
  }, [queryClient]);

  const plan = useCallback((step: StepRef, mode: PlanMode = "new") => navigate(planPath(step, mode)), [navigate]);

  const addToCalendar = useCallback(
    (step: StepRef) => {
      if (!available) setComingSoon(true);
      else if (!connection) setConnectFor({ step, notice: null });
      else plan(step);
    },
    [available, connection, plan],
  );

  const connect = useCallback(() => {
    if (!available) setComingSoon(true);
    else setConnectFor({ notice: null });
  }, [available]);

  const continueToGoogle = () => {
    const step = connectFor?.step;
    const returnPath = `${location}${window.location.search}`;
    saveHandoff({ origin, journalId: step?.journalId, actionId: step?.id, stepText: step?.text, returnPath });
    startConnect.mutate(
      { returnTo: returnUrl(returnPath) },
      { onError: () => setConnectFor((c) => (c ? { ...c, notice: "failed" } : c)) },
    );
  };

  // Cleared on open as well as close: a failure that lands after the dialog was dismissed
  // mid-request mustn't greet the next one.
  const openRemove = useCallback((step: StepRef) => {
    setRemoveError(null);
    setRemoving(step);
  }, []);

  const closeRemove = () => {
    setRemoving(null);
    setRemoveError(null);
  };

  const confirmRemove = () => {
    if (!removing) return;
    const step = removing;
    setRemoveError(null);
    removeSchedule.mutate(
      { actionId: step.id, scope: "future" },
      {
        onSuccess: () => {
          closeRemove();
          showPillToast({ message: "Removed from Google Calendar · the step stays in Momentum" });
        },
        onError: (err) => setRemoveError(calendarErrorCode(err) === "connection_ended" ? "connection_ended" : "failed"),
      },
    );
  };

  const value = useMemo(
    () => ({ available, connection, schedules, addToCalendar, connect, plan, remove: openRemove }),
    [available, connection, schedules, addToCalendar, connect, plan, openRemove],
  );

  const removingSchedule = removing ? schedules.get(removing.id) : undefined;

  return (
    <CalendarFlowContext.Provider value={value}>
      {children}
      <ConnectCalendarDialog
        open={!!connectFor}
        onOpenChange={(open) => !open && setConnectFor(null)}
        stepText={connectFor?.step?.text || undefined}
        notice={connectFor?.notice ?? null}
        continuing={startConnect.isPending || startConnect.isSuccess}
        onContinue={continueToGoogle}
      />
      <CalendarConnectedDialog
        open={!!connectedFor && !!connection}
        onOpenChange={(open) => !open && setConnectedFor(null)}
        connection={connection}
        stepText={connectedFor?.step?.text || undefined}
        onFindTime={
          connectedFor?.step
            ? () => {
                const step = connectedFor.step!;
                setConnectedFor(null);
                plan(step);
              }
            : undefined
        }
        disconnecting={disconnect.isPending}
        onDisconnect={() =>
          disconnect.mutate(undefined, {
            onSuccess: () => {
              const step = connectedFor?.step;
              setConnectedFor(null);
              setConnectFor({ step, notice: null });
            },
          })
        }
      />
      <RemoveFromCalendarDialog
        step={removing}
        schedule={removingSchedule}
        pending={removeSchedule.isPending}
        error={removeError}
        onCancel={closeRemove}
        onConfirm={confirmRemove}
      />
      <CalendarComingSoonDialog open={comingSoon} onOpenChange={setComingSoon} />
    </CalendarFlowContext.Provider>
  );
}

function RemoveFromCalendarDialog({
  step,
  schedule,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  step: StepRef | null;
  schedule: StepSchedule | undefined;
  pending: boolean;
  error: RemoveError;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const upcoming = schedule ? upcomingEvents(schedule) : [];
  const next = schedule ? nextEvent(schedule) : null;
  const what =
    schedule?.kind === "rhythm"
      ? `the ${upcoming.length} upcoming ${upcoming.length === 1 ? "date" : "dates"} of this routine`
      : upcoming.length > 1
        ? `its ${upcoming.length} upcoming events`
        : next
          ? `the event on ${formatDay(new Date(next.start))}`
          : "its event";

  return (
    <Dialog open={!!step} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent hideClose overlayClassName={calendarOverlayClass} className={cn(calendarDialogClass, "sm:max-w-[440px]")}>
        <div className="px-6 pb-7 pt-7 sm:px-8 sm:pt-8">
          <IconTile tone="red" size="lg" className="rounded-full">
            <Trash2 className="h-5 w-5" />
          </IconTile>
          <DialogTitle className="mt-5 font-display text-[22px] font-semibold tracking-[-0.02em] text-foreground">
            Remove from your calendar?
          </DialogTitle>
          <DialogDescription className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            This removes {what} from Google Calendar. The step stays in Momentum, and events that already happened stay as a
            record.
          </DialogDescription>
          {error && (
            <p role="alert" className="mt-5 flex gap-2 rounded-2xl bg-[#fbe6e6] px-4 py-3 text-[13.5px] leading-relaxed text-[#8f2a33]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {error === "connection_ended"
                ? "Your Google Calendar connection ended. Reconnect in Settings, then try again."
                : "Couldn't reach Google Calendar. Nothing was removed — try again."}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <OutlinePill onClick={onCancel}>Keep it</OutlinePill>
            <DarkPill loading={pending} onClick={onConfirm} className="bg-[#a8323e] hover:bg-[#a8323e]/90">
              Remove
            </DarkPill>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
